import { useCallback, useMemo, useState } from 'react';
import {
  CustomMultiSelectFilter,
  DateRangeFilter,
  FilterOption,
  FiltersBar,
  FiltersErrors,
  SimpleFiltersObjectValues,
  MultiToggleFilter,
  SwitchFilter,
} from '@britishcouncil/react-admin';

import { useCentresOptions } from 'hooks';
import { CentreTypeSelector } from 'api';
import {
  useSearchExaminersQuery,
  TrainingAndStandardisationFilterType,
  useTrainingSessionsQuery,
} from 'store';
import { TrainingAndStandardisationStatusBagde } from 'components';
import {
  trainingCategoryOptions,
  trainingStatusOptions,
  trainingTypeOptions,
} from 'types/dictionaries';

export type TrainingAndStandardisationReportFilters = {
  date: DateRangeFilter;
  standardisedNotCertified: SwitchFilter;
  trainingCategories: CustomMultiSelectFilter<number>;
  examinerIds: CustomMultiSelectFilter<number>;
  trainingStatuses: MultiToggleFilter<number>;
  trainingTypes: CustomMultiSelectFilter<number>;
  organisingCentreIds: CustomMultiSelectFilter<string>;
  mainSpeakingCentreIds: CustomMultiSelectFilter<string>;
  mainWritingCentreIds: CustomMultiSelectFilter<string>;
  trainingIds: CustomMultiSelectFilter<number>;
};

export type TrainingAndStandardisationReportFiltersBarProps = {
  initialFilterState: TrainingAndStandardisationFilterType;
  setFilterState: (filterState: TrainingAndStandardisationFilterType) => void;
};

export const TrainingAndStandardisationReportFiltersBar = ({
  initialFilterState,
  setFilterState,
}: TrainingAndStandardisationReportFiltersBarProps) => {
  const minCharactersToLoadExaminers: number = 3;

  const [examinerSearchText, setExaminerSearchText] = useState<string>('');
  const [errors, setErrors] = useState<FiltersErrors>();
  const [filtersValues, setFiltersValues] =
    useState<SimpleFiltersObjectValues<TrainingAndStandardisationReportFilters>>(
      initialFilterState
    );

  const organisingCentresOptions = useCentresOptions(CentreTypeSelector.BC);
  const mainCentresOptions = useCentresOptions(CentreTypeSelector.ALL);
  const isDateRangeMandatory = !filtersValues.standardisedNotCertified;

  const { data: trainingSessions } = useTrainingSessionsQuery();
  const trainingSessionOptions: FilterOption<number>[] = useMemo(
    () => trainingSessions?.map((t) => ({ text: t.name, value: t.id })) ?? [],
    [trainingSessions]
  );

  const {
    data,
    isFetching: areExaminersStillFetching,
    isError: fetchingExaminersError,
  } = useSearchExaminersQuery(examinerSearchText, {
    skip: examinerSearchText.length < minCharactersToLoadExaminers,
  });

  const newSearchExaminers: FilterOption<number>[] = useMemo(
    () =>
      data?.results?.map((e) => ({
        text: e.firstName + ' ' + e.lastName + ' (' + e.examinerNumber + ')',
        value: e.id,
      })) ?? [],
    [data]
  );

  const getFiltersDefinition: () => TrainingAndStandardisationReportFilters = useCallback(() => {
    return {
      date: {
        type: 'daterange',
        isMain: true,
        label: 'Date range',
        datePickerOptions: {
          placeholder: 'Select dates...',
          isClearable: true,
        },
      },
      standardisedNotCertified: {
        type: 'switch',
        isMain: true,
        label: 'Standardised not Certified',
        iconPopup: {
          icon: 'info circle',
          iconColor: 'blue',
          message:
            'Only completed Standardisation sessions for Examiners without valid Certification will be displayed',
        },
      },
      trainingCategories: {
        type: 'custom-multiselect',
        label: 'Category',
        options: trainingCategoryOptions,
        dropdownOptions: {
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
      },
      examinerIds: {
        type: 'custom-multiselect',
        label: 'Examiner',
        options: newSearchExaminers,
        dropdownOptions: {
          noResultsMessage: fetchingExaminersError
            ? 'Error in retrieving examiners...'
            : areExaminersStillFetching
              ? 'Loading...'
              : examinerSearchText.length < minCharactersToLoadExaminers
                ? 'Please type at least ' + minCharactersToLoadExaminers + ' characters'
                : 'No results found',
          placeholder: 'Search...',
          search: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
          onSearchChange: (_, { searchQuery }) => setExaminerSearchText(searchQuery),
        },
      },
      trainingStatuses: {
        type: 'multitoggle',
        label: 'Status',
        options: trainingStatusOptions.map(({ value }) => ({
          content: <TrainingAndStandardisationStatusBagde status={value} />,
          value,
        })),
      },
      trainingTypes: {
        type: 'custom-multiselect',
        label: 'Type',
        dropdownOptions: {
          placeholder: 'Select...',
          style: { maxWidth: '250px', width: '250px' },
        },
        options: trainingTypeOptions,
      },
      organisingCentreIds: {
        type: 'custom-multiselect',
        label: 'Organising centre',
        className: 'centre-dropdown-component',
        dropdownOptions: {
          placeholder: 'Search...',
          search: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
        options: organisingCentresOptions,
      },
      mainSpeakingCentreIds: {
        type: 'custom-multiselect',
        className: 'centre-dropdown-component',
        label: 'Examiner main speaking centre',
        dropdownOptions: {
          placeholder: 'Search...',
          search: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
        options: mainCentresOptions,
      },
      mainWritingCentreIds: {
        type: 'custom-multiselect',
        className: 'centre-dropdown-component',
        label: 'Examiner main writing centre',
        dropdownOptions: {
          placeholder: 'Search...',
          search: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
        options: mainCentresOptions,
      },
      trainingIds: {
        type: 'custom-multiselect',
        label: 'Session name',
        dropdownOptions: {
          placeholder: 'Search...',
          search: true,
          selectedOnTop: true,
          isClearable: true,
          style: { maxWidth: '250px', width: '250px' },
        },
        options: trainingSessionOptions,
      },
    };
  }, [
    newSearchExaminers,
    fetchingExaminersError,
    areExaminersStillFetching,
    examinerSearchText.length,
    organisingCentresOptions,
    mainCentresOptions,
    trainingSessionOptions,
  ]);

  const filtersDefinition = useMemo(() => getFiltersDefinition(), [getFiltersDefinition]);

  const onFiltersApply = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TrainingAndStandardisationReportFilters>) => {
      const isDateRangeSet = filtersValues.date?.[0] && filtersValues.date?.[1];
      if (!isDateRangeMandatory || isDateRangeSet) {
        setFilterState({
          ...filtersValues,
        });
      } else {
        setErrors({ date: 'Date range is required' });
      }
    },
    [isDateRangeMandatory, setFilterState]
  );

  const onFiltersChange = useCallback(
    (filtersValues: SimpleFiltersObjectValues<TrainingAndStandardisationReportFilters>) => {
      const isDateRangeSet = filtersValues.date?.[0] && filtersValues.date?.[1];
      setFiltersValues(filtersValues);

      if (!isDateRangeMandatory || isDateRangeSet) {
        setErrors({});
      }
    },
    [isDateRangeMandatory]
  );

  return (
    <FiltersBar
      value={filtersValues}
      filters={filtersDefinition}
      errors={errors}
      clearable
      expanded={true}
      onFiltersApply={onFiltersApply}
      onFiltersChange={onFiltersChange}
      defaultValues={initialFilterState}
    />
  );
};
